'use client';

import {
  Button,
  Flex,
  ButtonGroup,
  Dialog,
  InputChips,
  ResultListItem,
} from '@/_components';
import {
  useState,
  useEffect,
  useContext,
  useRef,
  type FC,
  ChangeEvent,
  useMemo,
} from 'react';
import type { EditParticipantsDialogProps } from './EditParticipantsDialog.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { updateConversation } from '@/_api/admin-conversations';
import { UserContext } from '@/_contexts/UserProvider';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';

export const EditParticipantsDialog: FC<EditParticipantsDialogProps> = ({
  isOpen,
  onClose,
  conversation,
}) => {
  const firmUserSelectorRef = useRef<HTMLDivElement | null>(null);

  const { addToast } = useContext(ToastContext);
  const { firmUsers } = useContext(FirmSettingsContext);
  const { token } = useContext(UserContext);
  const { reloadMessages } = useContext(AdminConversationsContext);

  const initialChips: Set<string> = useMemo(
    () => new Set(conversation?.advisor_ids?.map((id) => id.toString())),
    [conversation?.advisor_ids],
  );

  useEffect(() => {
    setChips(initialChips);

    return () => {
      setChips(initialChips);
    };
  }, [initialChips, isOpen]);

  const [filter, setFilter] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [chips, setChips] = useState<Set<string>>(initialChips);

  const onChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);

  const results: Array<ResultListItem> = useMemo(
    () =>
      firmUsers?.map((firmUser) => {
        return { name: firmUser.name, value: firmUser.id.toString() };
      }) || [],
    [firmUsers],
  );

  const onUpdate = async () => {
    try {
      setLoading(true);
      const advisorIds: Array<number> = Array.from(chips).map((c) =>
        parseInt(c),
      );

      const { error } = await updateConversation({
        conversationId: conversation?.id!,
        advisor_ids: advisorIds,
        token,
      });

      if (!error && !!conversation) {
        reloadMessages(true);
        onClose();
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      isOverflowing={true}
      title="Manage Participants"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={8} ref={firmUserSelectorRef}>
          <InputChips
            label="Add or remove participants in this conversation."
            onChange={onChange}
            isDisabled={isLoading}
            value={filter}
            placeholder="Select users"
            chips={chips}
            chipNames={results}
            setChips={setChips}
            results={results}
          />
          <ButtonGroup align="right">
            <Button
              intent="secondary"
              onClick={onClose}
              value="Cancel"
              isDisabled={isLoading}
            />
            <Button
              intent="primary"
              onClick={onUpdate}
              value="Save"
              isDisabled={isLoading || !chips.size}
              isLoading={isLoading}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
